import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useSaveCartAsQuote,
  type SaveCartAsQuoteResponse,
} from "@/api/queries";

import { Button, IngotModal } from "@/ingot";
/**
 * Modal dialog for the "Uložit jako nabídku" CTA in the cart summary.
 *
 * KAN-1673 — saves a price quote (locked prices, PDF). The optional name
 * is the customer's own reference on that quote; the input starts empty
 * and ``defaultName`` is only a placeholder hint (KAN-1652) — blank sends
 * nothing, the quote number identifies it.
 * Caption surfaces the validity window resolved BE-side from
 * ``QuoteSettings`` and passed in via the ``quoteValidityDays`` prop.
 */
export function SaveAsQuoteModal({
  defaultName,
  quoteValidityDays,
  open,
  onClose,
  onSaved,
}: {
  /** Placeholder hint only — never sent. */
  defaultName: string;
  /**
   * Validity window (days) the BE will stamp on ``expires_at``. Sourced
   * from ``cart.quote_validity_days`` so the modal caption matches the
   * resolved ``QuoteSettings`` value (tenant override → platform default → 14).
   */
  quoteValidityDays: number;
  open: boolean;
  onClose: () => void;
  onSaved: (result: SaveCartAsQuoteResponse) => void;
}): JSX.Element | null {
  const { t } = useTranslation("cart");
  const save = useSaveCartAsQuote();
  // KAN-1652 — pole začíná PRÁZDNÉ, výchozí název je jen placeholder.
  // Předvyplněný text zákazník přepisoval psaním doprostřed a vznikl
  // slepenec „Nabídka U-…Test nabidka dil A-553297".
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Re-seed the input when the modal opens for a different cart (or
  // server suggests a new default name).
  useEffect(() => {
    if (open) {
      setName("");
      setError(null);
    }
  }, [open, defaultName]);

  if (!open) return null;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    save.mutate(
      { name: trimmed || undefined },
      {
        onSuccess: (data) => {
          onSaved(data);
          onClose();
        },
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <IngotModal
      title={t("summary.saveAsQuoteModal.title", {
        defaultValue: "Uložit jako nabídku",
      })}
      onClose={onClose}
      closeLabel={t("summary.saveAsQuoteModal.close", {
        defaultValue: "Zavřít",
      })}
      width={448}
      testId="save-as-quote-modal"
    >
      <form onSubmit={onSubmit}>
        <p className="text-xs text-ink-3">
          {t("summary.saveAsQuoteModal.lead", {
            defaultValue:
              "Snímek aktuálního košíku, který se uloží do Mých projektů.",
          })}
        </p>

        <label className="mt-4 block text-xs font-medium text-ink-2">
          {t("summary.saveAsQuoteModal.nameLabel", {
            defaultValue: "Název nabídky",
          })}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={defaultName}
            maxLength={64}
            autoFocus
            className="mt-1 w-full rounded border border-border bg-bg px-2 py-2 text-sm text-ink focus:border-ink-3 focus:outline-none"
            data-testid="save-as-quote-modal-name"
          />
        </label>

        <p className="mono mt-2 text-[10.5px] uppercase tracking-[0.06em] text-ink-4">
          {t("summary.saveAsQuoteModal.validityCaption", {
            count: quoteValidityDays,
          })}
        </p>

        {error && (
          <p
            className="mt-3 text-[11px] text-danger"
            data-testid="save-as-quote-modal-error"
          >
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={save.isPending}
            data-testid="save-as-quote-modal-cancel"
          >
            {t("summary.saveAsQuoteModal.cancel", { defaultValue: "Zrušit" })}
          </Button>
          <Button
            type="submit"
            variant="accent"
            disabled={save.isPending}
            data-testid="save-as-quote-modal-submit"
          >
            {save.isPending
              ? t("summary.saveAsQuotePending", { defaultValue: "Ukládám…" })
              : t("summary.saveAsQuoteModal.submit", {
                  defaultValue: "Uložit nabídku",
                })}
          </Button>
        </div>
      </form>
    </IngotModal>
  );
}
